import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { invoke } from "@tauri-apps/api/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";

import { getEnv } from "@ando/core";
import { AndoWordmark } from "@ando/ui";
import { AndoBrainLoader, LoginBlob } from "@ando/ui/blob";

import {
  DesktopLoginForm,
  useDesktopIdpLogin,
  useDesktopOAuth,
} from "@/features/auth/desktop";

/**
 * Open Ando's password recovery UI in the configured web app. The web
 * BFF owns reset delivery and the custom password form in both runtimes.
 */
function getPasswordResetUrl(): string | null {
  const webUrl = getEnv().webUrl;
  return webUrl ? `${webUrl.replace(/\/$/, "")}/password/reset` : null;
}

/**
 * `/login` — dual-mode handoff.
 *
 * - **Web build** — handoff to the web app's PKCE flow at
 *   ``apps/web/login`` (BFF owns the cookie-bearing callback). The
 *   BFF origin is read from the runtime config (``getEnv().webUrl``,
 *   sourced from ``/runtime-config.js`` rendered at container boot)
 *   — NEVER from ``import.meta.env`` (build-time), or the same hashed
 *   bundle would serve every environment with a baked URL and a
 *   ``localhost:3000`` fallback could leak into prod.
 * - **Tauri desktop build** — the SPA itself drives the OAuth flow
 *   via ``useDesktopOAuth``: a click on the CTA opens the system
 *   browser (RFC 8252), the ``ando://callback`` deep-link bounces
 *   back into the binary, and ``/auth/desktop/exchange`` returns
 *   the Bearer token bundle. No bounce out of the Tauri window.
 */
export const Route = createFileRoute("/login")({
  component: LoginEntry,
});

function LoginEntry() {
  if (getEnv().isTauri) {
    return <DesktopLogin />;
  }
  return <WebLoginRedirect />;
}

function WebLoginRedirect() {
  const { i18n } = useLingui();
  useEffect(() => {
    const webBase = getEnv().webUrl || "";
    const search = window.location.search;
    window.location.href = webBase
      ? `${webBase.replace(/\/$/, "")}/login${search}`
      : `/login${search}`;
  }, []);

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-cream">
      <AndoBrainLoader size="md" label={i18n._(msg`Reindirizzamento al login…`)} />
    </div>
  );
}

/**
 * Native login screen — minimal but on-brand. Single CTA opens the
 * default browser; once the deep-link callback lands we route to the
 * workspace.
 */
function DesktopLogin() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  // ``useDesktopOAuth`` still owns the vault-hydrate path (returning
  // users skip the form) AND the generic SSO fallback ("Continua con
  // SSO nel browser" inside DesktopLoginForm). Its ``isLoading`` also
  // covers the initial hydrate flash.
  const {
    tokens,
    isLoading: oauthLoading,
    error: oauthError,
    signIn,
  } = useDesktopOAuth();
  // ``useDesktopIdpLogin`` fetches the per-provider IdP list from the
  // BE and drives the Zitadel IdP-intent flow on click. Its errors
  // merge into the same surface as the OAuth ones so the user sees
  // ONE error line regardless of which path failed.
  const desktopIdp = useDesktopIdpLogin({
    onTokens: async () => {
      void navigate({ to: "/workspace/conversations" });
    },
  });

  // As soon as tokens land (either from a fresh login or vault
  // hydrate) bounce into the authenticated shell.
  useEffect(() => {
    if (tokens) {
      void navigate({ to: "/workspace/conversations" });
    }
  }, [tokens, navigate]);

  const resetUrl = getPasswordResetUrl();

  // Open the Ando password-reset URL in the SYSTEM browser (via the
  // same Rust command the OAuth flow uses) — never in the webview.
  const onForgotPassword = useCallback(() => {
    if (!resetUrl) return;
    void invoke("start_oauth_login", { authorizeUrl: resetUrl });
  }, [resetUrl]);

  // Merge the two error surfaces into ONE — first-error-wins so the
  // user isn't told two different things about the same click.
  const error = oauthError || desktopIdp.error;

  return (
    <div className="relative flex h-screen w-screen flex-col items-center justify-end overflow-hidden bg-cream pb-[12vh] font-sans text-ink-1">
      {/* Ambient brand blob — pinned to the upper portion of the
          window so the login card sits beneath it. Pattern
          Granola/Linear desktop login with hero-up / form-down stack. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 flex justify-center opacity-80"
      >
        <div className="-translate-y-[6vh] h-[55vh] max-h-[600px] w-[55vh] max-w-[600px]">
          <LoginBlob size="hero" />
        </div>
      </div>

      {/* Warm gradient wash to soften the background, then the card */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,hsl(22_95%_98%/0.7)_55%,hsl(22_95%_96%)_100%)]"
      />

      <div className="relative z-10 w-full max-w-[420px] rounded-2xl border border-hairline bg-white/85 p-10 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_18px_40px_-12px_rgba(0,0,0,0.12)] backdrop-blur-xl">
        {oauthLoading ? (
          // Vault hydrate OR active SSO-fallback exchange. Swap the
          // whole card for the brand loader so a stale form doesn't
          // flash before we navigate away (hydrate hit) or before the
          // callback lands (SSO fallback in flight).
          <div className="flex flex-col items-center gap-6 py-2">
            <h1 className="text-center font-display font-semibold text-[22px] text-ink-1 leading-tight tracking-[-0.02em]">
              {i18n._(msg`Apertura del browser…`)}
            </h1>
            <AndoBrainLoader size="sm" />
            <p className="max-w-[28ch] text-center text-[12px] text-ink-3">
              {i18n._(msg`Completa l'accesso nel browser. Tornerai qui automaticamente.`)}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 text-center">
              <div className="mb-5 flex justify-center">
                <AndoWordmark fontSize={30} glow={false} />
              </div>
              <h1 className="font-display font-semibold text-[26px] text-ink-1 leading-tight tracking-[-0.02em]">
                {i18n._(msg`Bentornato`)}
              </h1>
              <p className="mt-1.5 text-[13px] text-ink-3">
                {i18n._(msg`Accedi al tuo workspace Ando`)}
              </p>
            </div>

            <DesktopLoginForm
              onAuthenticated={() => {
                void navigate({ to: "/workspace/conversations" });
              }}
              onSsoFallback={(opts) => {
                void signIn(opts);
              }}
              idps={desktopIdp.idps}
              isLoadingIdps={desktopIdp.isLoading}
              onRetryIdps={desktopIdp.refetch}
              onIdpClick={desktopIdp.startWithProvider}
              passwordResetUrl={resetUrl}
              onForgotPassword={resetUrl ? onForgotPassword : undefined}
            />
          </>
        )}

        {error ? (
          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50/70 p-3 text-rose-900 text-xs">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
